/**
 * Content Ideas — pure helpers (labels, storyboard text, composer seed).
 *
 * Idea text is external research content. It is rendered as PLAIN TEXT
 * everywhere (React escapes it); `toPlainText` additionally strips any markup
 * that slipped into the research copy via DOMPurify, so a stray `<b>` never
 * shows up as literal tags and nothing is ever injected as HTML.
 *
 * @module lib/contentIdeas
 */

import DOMPurify from "dompurify";
import type { IdeaDetail, IdeaFacet, IdeaShot, StoryboardBeat } from "../api/contentIdeas";
import { useCreateFlowStore } from "../stores/useCreateFlowStore";
import type { Account, Platform, Template } from "../types/social";
import { seedComposerFromTemplate } from "./composerSeed";

/** Facets offered as filters, in display order (format is a dropdown — ~100 values). */
export const FILTER_FACETS: IdeaFacet[] = [
  "vertical",
  "channel",
  "idea_type",
  "trend_status",
  "difficulty",
  "humor_format",
  "hook_type",
  "pillar",
  "format",
];

export const FACET_LABELS: Record<IdeaFacet, string> = {
  vertical: "Industry",
  channel: "Channel",
  format: "Format",
  difficulty: "Effort",
  trend_status: "Trend",
  idea_type: "Type",
  humor_format: "Humor style",
  hook_type: "Hook",
  pillar: "Pillar",
};

const VALUE_LABELS: Record<string, string> = {
  universal: "Any business",
  medspa: "Med spa",
  bar_tavern: "Bar & tavern",
  digital_marketing: "Digital marketing",
  local_business: "Local business",
  both: "Organic + paid",
  organic: "Organic",
  paid: "Paid",
  rising: "Rising",
  peaking: "Peaking",
  evergreen: "Evergreen",
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  behind_the_scenes: "Behind the scenes",
  ugc: "UGC",
  how_to: "How-to",
};

/** Human label for a facet value (`bar_tavern` → "Bar & tavern"). */
export function facetValueLabel(value: string): string {
  if (VALUE_LABELS[value]) return VALUE_LABELS[value];
  const spaced = value.replace(/_/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : value;
}

/** Facet values sorted for display: trend/effort in natural order, others by count then name. */
const NATURAL_ORDER: Partial<Record<IdeaFacet, string[]>> = {
  trend_status: ["rising", "peaking", "evergreen"],
  difficulty: ["easy", "medium", "hard"],
};

export function facetOptions(facet: IdeaFacet, counts: Record<string, number> | undefined): [string, number][] {
  const entries = Object.entries(counts ?? {}).filter(([v]) => v && v !== "null");
  const order = NATURAL_ORDER[facet];
  if (order) {
    const rank = (v: string) => (order.includes(v) ? order.indexOf(v) : order.length);
    return entries.sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]));
  }
  if (facet === "format" || facet === "pillar") return entries.sort((a, b) => a[0].localeCompare(b[0]));
  return entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  instagram_reels: "Instagram Reels",
  facebook: "Facebook",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  youtube_shorts: "YouTube Shorts",
  threads: "Threads",
  pinterest: "Pinterest",
  google_business: "Google Business",
};

export function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform] ?? facetValueLabel(platform);
}

/** Stored short-form aliases → the connected-account platform they post to. */
export function basePlatform(platform: string): string {
  if (platform === "instagram_reels") return "instagram";
  if (platform === "youtube_shorts") return "youtube";
  return platform;
}

/** Badge classes per trend status (every branch carries its dark: pair). */
export const TREND_BADGE: Record<string, string> = {
  rising: "bg-success-bg text-success-strong",
  peaking: "bg-warning-bg text-warning-strong",
  evergreen: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
};
export const NEUTRAL_BADGE = "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200";

const MARKUP_RE = /<[a-z!/?]/i;

/** Plain text from research copy: markup stripped via DOMPurify, entities decoded. */
export function toPlainText(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (!MARKUP_RE.test(text)) return text;
  const fragment = DOMPurify.sanitize(text, { ALLOWED_TAGS: [], KEEP_CONTENT: true, RETURN_DOM_FRAGMENT: true });
  return (fragment.textContent ?? "").trim();
}

/** Only http(s) links are rendered as anchors. */
export function safeHttpUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

/** "Hook", "Beat 2" — a numeric or missing `beat` falls back to its position. */
export function beatLabel(beat: StoryboardBeat, index: number): string {
  const raw = beat.beat;
  if (typeof raw === "string" && raw.trim() && !/^\d+$/.test(raw.trim())) return toPlainText(raw);
  return `Beat ${index + 1}`;
}

/** The fields shown for each beat, in order (missing ones are skipped). */
export const BEAT_FIELDS: { key: "shot" | "text_overlay" | "audio"; label: string }[] = [
  { key: "shot", label: "Shot" },
  { key: "text_overlay", label: "On-screen text" },
  { key: "audio", label: "Audio" },
];

/** Caption draft for "Write it now": the hook, then the call to action. */
export function ideaCaption(idea: Pick<IdeaDetail, "hook" | "cta_pattern">): string {
  return [toPlainText(idea.hook).trim(), toPlainText(idea.cta_pattern).trim()].filter(Boolean).join("\n\n");
}

/**
 * Seed the post composer from an idea (same stores the Templates "Use" button
 * seeds): caption = hook + CTA, targets = the client's active accounts on the
 * idea's networks. The idea id rides along in the create-flow store so the
 * saved post carries `metadata.content_idea_id` (the pick itself is counted by
 * `POST /api/content-ideas/{id}/use`, not again on save).
 */
export function seedComposerFromIdea(idea: IdeaDetail, accounts: Account[] = []): void {
  const platforms = [...new Set((idea.platforms ?? []).map(basePlatform))] as Platform[];
  const template: Template = {
    id: idea.id,
    org_id: String(idea.org_id),
    name: idea.title,
    description: null,
    content: ideaCaption(idea),
    media_urls: [],
    hashtags: [],
    platforms,
    category: idea.format,
    is_global: idea.is_global,
    usage_count: idea.usage_count,
    metadata: {},
    created_at: idea.created_at,
    updated_at: idea.updated_at,
  };
  seedComposerFromTemplate(template, accounts);
  useCreateFlowStore.getState().setContentIdea(idea.id);
}

const SOURCE_ASSET_KIND_LABELS: Record<string, string> = {
  dam: "Media library",
  content_library: "Content library",
  drive: "Drive",
  footage: "Raw footage",
};

/** Human label for a `source_assets[].kind` value (`dam` → "Media library"). */
export function sourceAssetKindLabel(kind: string): string {
  return SOURCE_ASSET_KIND_LABELS[kind] ?? facetValueLabel(kind);
}

/** Today's date as YYYY-MM-DD in the browser's zone (the date input's default/min). */
export function todayIso(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ── v2: shot list, evidence, score, warnings ───────────────────────────────────

/** Min-score choices for the library filter (ICE score is 0-100). */
export const MIN_SCORE_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Any score" },
  { value: 40, label: "40 and up" },
  { value: 60, label: "60 and up" },
  { value: 80, label: "80 and up" },
];

/** "15 s", "2.5 s" — whole seconds drop the decimal. */
export function formatSeconds(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} s`;
}

/** Seconds without the unit, for ranges ("0–3 s"). */
function secondsBare(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** "0–3 s" */
export function formatRange(start: number, end: number): string {
  return `${secondsBare(start)}–${secondsBare(end)} s`;
}

export interface TimedShot {
  shot: IdeaShot;
  start: number;
  end: number;
}

/** Shots in order with their cumulative reel time derived from each `duration_s`. */
export function shotTimeline(shots: IdeaShot[]): TimedShot[] {
  let cursor = 0;
  return [...shots]
    .sort((a, b) => a.shot_no - b.shot_no)
    .map((shot) => {
      const start = cursor;
      cursor += Math.max(0, shot.duration_s || 0);
      return { shot, start, end: cursor };
    });
}

/** The human part of a "code: message" storyboard warning. */
export function warningMessage(raw: string): string {
  const text = toPlainText(raw);
  const i = text.indexOf(": ");
  return (i >= 0 ? text.slice(i + 2) : text).trim();
}

/** 12400 → "12,400"; 1_250_000 → "1.3M". */
export function formatCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  return Math.round(value).toLocaleString("en-US");
}

/** Performance index as a multiple of typical: 3.24 → "×3.2". */
export function formatIndex(value: number): string {
  return `×${value.toFixed(1)}`;
}

/** Share rate (fraction) as a percent: 0.041 → "4.1%". */
export function formatShareRate(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

const EVIDENCE_KIND_LABELS: Record<string, string> = {
  archive_post: "Archive post",
  research: "Research",
  trend: "Trend",
};

export function evidenceKindLabel(kind: string): string {
  return EVIDENCE_KIND_LABELS[kind] ?? facetValueLabel(kind);
}
